import React, { useMemo } from "react";
import { connect } from "react-redux";

import { ColumnType } from "antd/es/table";
import { compareAsc } from "date-fns";
import { useIntl } from "react-intl";

import Email from "@mapmycustomers/shared/types/email/Email";

import Table from "@app/component/Table";
import { getEmailQueue, isEmailQueueLoading } from "@app/store/email";
import { RootState } from "@app/store/rootReducer";
import { formatRawDate } from "@app/util/formatters";
import { parseApiDate } from "@app/util/parsers";

import CancelEmailAction from "./CancelEmailAction";
import EmailQueueActions from "./EmailQueueActions";
import EmailQueueEmpty from "./EmailQueueEmpty";
import styles from "./EmailQueueTable.module.scss";
import EmailStatusTag from "./EmailStatusTag";
import messages from "./messages";

const getEmailName = (email: Email) => email.recipientAddress[0].name;

const stringSorter = (a: null | string | undefined, b: null | string | undefined) =>
  (a ?? "").localeCompare(b ?? "");

const dateSorter = (a: null | string, b: null | string) => {
  if (a === b) {
    return 0;
  }
  if (!a) {
    return -1;
  }
  if (!b) {
    return 1;
  }
  return compareAsc(parseApiDate(a), parseApiDate(b));
};

const EmailQueueTable = ({ emails, emailsLoading }: ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();

  const columns: ColumnType<Email>[] = useMemo(() => {
    return [
      {
        key: "name",
        render: (_, email) => getEmailName(email),
        sorter: (a, b) => stringSorter(getEmailName(a), getEmailName(b)),
        title: intl.formatMessage(messages.nameColumnTitle),
      },
      {
        key: "email",
        render: (_, email) => email.recipientAddress[0].email,
        sorter: (a, b) => stringSorter(a.recipientAddress[0].email, b.recipientAddress[0].email),
        title: intl.formatMessage(messages.emailColumnTitle),
      },
      {
        key: "subject",
        render: (_, email) => email.subject,
        sorter: (a, b) => stringSorter(a.subject, b.subject),
        title: intl.formatMessage(messages.subjectColumnTitle),
      },
      {
        key: "status",
        render: (_, email) => <EmailStatusTag email={email} />,
        sorter: (a, b) => stringSorter(a.status, b.status),
        title: intl.formatMessage(messages.statusColumnTitle),
      },
      {
        key: "action",
        render: (_, email) => <CancelEmailAction email={email} />,
        title: (
          <span className={styles.actionColumnTitle}>
            <span>{intl.formatMessage(messages.actionColumnTitle)}</span>
            <EmailQueueActions />
          </span>
        ),
      },
      {
        key: "sentDate",
        render: (_, email) => formatRawDate(email.date, "PPp"),
        sorter: (a, b) => dateSorter(a.date, b.date),
        title: intl.formatMessage(messages.sentDateColumnTitle),
      },
    ];
  }, [intl]);

  if (!emails.length && !emailsLoading) {
    return <EmailQueueEmpty className={styles.content} />;
  }

  return (
    <Table<Email>
      className={styles.content}
      columns={columns}
      dataSource={emails}
      loading={emailsLoading}
      noHover
      pagination={false}
      rowKey="id"
      scrollable
      showBorder
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  emails: getEmailQueue(state),
  emailsLoading: isEmailQueueLoading(state),
});

export default connect(mapStateToProps)(EmailQueueTable);
